import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import { useIsFetching } from '@tanstack/react-query'
import KNAdvancedDropdown from 'components/KNAdvancedDropdown/KNAdvancedDropdown'
import { useCarriersView } from 'context/CarriersViewContext'
import { type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { CARRIERS_QUERY_KEY } from './CarrierManager.service'

const CarrierFilters = (): ReactElement => {
  const { t } = useTranslation()
  const { filters, filtersDirty, setFilters, resetFilters } = useCarriersView()
  const isFetching = useIsFetching({ queryKey: [CARRIERS_QUERY_KEY] })

  return (
    <Box sx={{ position: 'relative' }}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={filters.keywords ?? []}
            onChange={(event, newValue): void => {
              setFilters({
                keywords: newValue,
              })
            }}
            renderValue={(tagValue, getTagProps): ReactElement[] =>
              tagValue.map((option, index) => (
                // eslint-disable-next-line react/jsx-key
                <Chip {...getTagProps({ index })} variant="outlined" size="small" label={option} />
              ))
            }
            renderInput={(params): ReactElement => (
              <TextField {...params} label={t('screens.cs.carrier_manager.filters.search')} />
            )}
            disabled={isFetching > 0}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 2 }}>
          <KNAdvancedDropdown
            label={t('screens.cs.carrier_manager.filters.integrations')}
            options={['NONE', 'I_BROKER', 'I_BROKER_V2'].map((integration) => ({
              value: integration,
              label: t(`screens.cs.carrier_manager.integrations.${integration}`),
            }))}
            value={filters.integrations}
            onChange={(newValue): void => {
              setFilters({
                integrations: newValue,
              })
            }}
            onDelete={(value) => {
              setFilters({
                integrations: filters.integrations.filter((integration) => integration !== value),
              })
            }}
            size="small"
            multiple
            disabled={isFetching > 0}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 2 }}>
          <KNAdvancedDropdown
            label={t('screens.cs.carrier_manager.filters.telematics')}
            options={['NONE', 'S3PWEB', 'NICPLACE', 'MACRO_POINT'].map((telematics) => ({
              value: telematics,
              label: t(`screens.cs.carrier_manager.telematics.${telematics}`),
            }))}
            value={filters.telematics}
            onChange={(newValue): void => {
              setFilters({
                telematics: newValue,
              })
            }}
            onDelete={(value) => {
              setFilters({
                telematics: filters.telematics.filter((telematics) => telematics !== value),
              })
            }}
            size="small"
            multiple
            disabled={isFetching > 0}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 2 }}>
          <KNAdvancedDropdown
            label={t('screens.cs.carrier_manager.filters.features')}
            options={[
              {
                value: 'shortest_routes',
                label: t(`screens.cs.carrier_manager.card.columns.shortest_routes`),
              },
              {
                value: 'status_automation',
                label: t(`screens.cs.carrier_manager.card.columns.status_automation`),
              },
            ]}
            value={filters.features}
            onChange={(newValue): void => {
              setFilters({
                features: newValue,
              })
            }}
            onDelete={(value) => {
              setFilters({
                features: filters.features.filter((feature) => feature !== value),
              })
            }}
            size="small"
            multiple
            disabled={isFetching > 0}
          />
        </Grid>
      </Grid>
      {filtersDirty && (
        <Button
          size="small"
          variant="text"
          color="primary"
          onClick={resetFilters}
          sx={{
            padding: 0,
            minHeight: 'auto',
          }}
        >
          {t('general.reset_filters')}
        </Button>
      )}
    </Box>
  )
}

export default CarrierFilters
